"use client";

import Link from "next/link";
import { useState } from "react";
import {
  sektoren,
  sektorGruppenTitel,
  fragen,
  einstufen,
  type Antworten,
  type Ergebnis,
  type Empfehlung,
  type ErgebnisKey,
  type Sektorgruppe,
} from "@/lib/nis2-check";
import { fristen } from "@/lib/nis2";
import { trackEvent, TRACKING_EVENTS } from "@/lib/tracking";
import { Icon } from "@/components/ui/Icon";

/**
 * Der Betroffenheitscheck – das Lead-Magnet aus Playbook 3.3.
 *
 * Sechs Fragen, eine Ersteinschätzung. Die Logik liegt getrennt in
 * lib/nis2-check.ts und ist dort gegen die Personas des Playbooks geprüft.
 *
 * Das Ergebnis wird im Klick-Handler berechnet, nicht in einem Effect: So
 * wird das Mess-Ereignis genau einmal ausgelöst, auch wenn React im
 * Entwicklungsmodus Effects doppelt ausführt.
 */

const GRUPPEN: Sektorgruppe[] = ["anlage1", "anlage2", "groessenunabhaengig", "keiner"];
const GESAMT = fragen.length + 1;

const TON: Record<ErgebnisKey, string> = {
  wesentlich: "bg-surface-2 text-brand-ink ring-brand-400/25",
  wichtig: "bg-surface-2 text-brand-ink ring-brand-400/25",
  groessenunabhaengig: "bg-surface-2 text-brand-ink ring-brand-400/25",
  grenzfall: "bg-amber-400/15 text-amber-700 ring-amber-400/30 dark:text-amber-300",
  indirekt: "bg-cyan-400/15 text-accent ring-cyan-400/30",
  nicht: "bg-surface-3 text-fg-muted ring-line",
};

function mitAnlass(e: Empfehlung, anlass: string): string {
  return e.href.startsWith("/kontakt") ? `/kontakt?thema=nis2&anlass=${encodeURIComponent(anlass)}` : e.href;
}

export function Nis2Check() {
  const [schritt, setSchritt] = useState(0);
  const [sektor, setSektor] = useState("");
  const [antworten, setAntworten] = useState<Partial<Antworten>>({});
  const [ergebnis, setErgebnis] = useState<Ergebnis | null>(null);

  function sektorBestaetigen() {
    if (!sektor) return;
    setAntworten((a) => ({ ...a, sektor }));
    setSchritt(1);
  }

  function antworte(id: keyof Antworten, value: string) {
    const neu = { ...antworten, sektor, [id]: value } as Partial<Antworten>;
    setAntworten(neu);
    if (schritt < fragen.length) {
      setSchritt(schritt + 1);
      return;
    }
    const e = einstufen(neu as Antworten);
    setErgebnis(e);
    trackEvent(TRACKING_EVENTS.nis2Check, { ergebnis: e.key });
  }

  function neuStarten() {
    setSchritt(0);
    setSektor("");
    setAntworten({});
    setErgebnis(null);
  }

  if (ergebnis) return <ErgebnisAnsicht e={ergebnis} onNeu={neuStarten} />;

  const frage = schritt > 0 ? fragen[schritt - 1] : null;

  return (
    <div className="card-highlight overflow-hidden" aria-live="polite">
      <div className="border-b border-line bg-brand-500/5 px-6 py-5 md:px-8">
        <div className="flex items-center justify-between gap-4 text-sm">
          <span className="font-semibold text-fg">
            Frage {schritt + 1} von {GESAMT}
          </span>
          {schritt > 0 && (
            <button
              type="button"
              onClick={() => setSchritt(schritt - 1)}
              className="inline-flex items-center gap-1 text-fg-subtle hover:text-fg"
            >
              <Icon name="arrow-right" size={14} className="rotate-180" /> zurück
            </button>
          )}
        </div>
        <div className="mt-3 h-1.5 w-full rounded-full bg-surface-3">
          <div
            className="h-1.5 rounded-md bg-brand-500 transition-all duration-300"
            style={{ width: `${((schritt + 1) / GESAMT) * 100}%` }}
          />
        </div>
      </div>

      <div className="p-6 md:p-8">
        {schritt === 0 ? (
          <div>
            <label htmlFor="sektor" className="font-display text-xl font-semibold text-fg">
              In welchem Bereich ist Ihr Unternehmen hauptsächlich tätig?
            </label>
            <p className="mt-1.5 text-sm text-fg-muted">
              Maßgeblich ist die Haupttätigkeit. Bei Unsicherheit den Bereich wählen, mit dem Sie den meisten Umsatz machen.
            </p>
            <select
              id="sektor"
              value={sektor}
              onChange={(e) => setSektor(e.target.value)}
              className="mt-5 w-full rounded-xl border border-line bg-surface-2 px-4 py-3.5 text-base text-fg focus:border-brand-400/60 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            >
              <option value="">Bitte wählen …</option>
              {GRUPPEN.map((g) => (
                <optgroup key={g} label={sektorGruppenTitel[g]}>
                  {sektoren
                    .filter((s) => s.gruppe === g)
                    .map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
            <button
              type="button"
              onClick={sektorBestaetigen}
              disabled={!sektor}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-brand-500 px-7 py-3.5 font-semibold text-white shadow-cta transition-all hover:bg-brand-400 disabled:pointer-events-none disabled:opacity-40"
            >
              Weiter <Icon name="arrow-right" size={18} />
            </button>
          </div>
        ) : (
          frage && (
            <fieldset>
              <legend className="font-display text-xl font-semibold text-fg">{frage.frage}</legend>
              <p className="mt-1.5 text-sm text-fg-muted">{frage.hilfe}</p>
              <div className="mt-5 grid gap-2.5">
                {frage.optionen.map((o) => {
                  const gewaehlt = antworten[frage.id] === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => antworte(frage.id, o.value)}
                      className={`flex items-center justify-between gap-3 rounded-xl border p-4 text-left transition-all ${
                        gewaehlt ? "border-brand-400/60 bg-brand-500/10" : "border-line bg-surface-2 hover:border-brand-400/40"
                      }`}
                    >
                      <span className="text-sm font-medium text-fg">{o.label}</span>
                      <Icon name="arrow-right" size={16} className="shrink-0 text-fg-subtle" />
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )
        )}
        <p className="mt-8 flex items-start gap-2 text-xs leading-relaxed text-fg-subtle">
          <Icon name="lock" size={14} className="mt-0.5 shrink-0" />
          Ihre Angaben verlassen Ihren Browser nicht. Es wird nichts gespeichert oder übertragen.
        </p>
      </div>
    </div>
  );
}

function EmpfehlungKarte({ e, anlass, primaer }: { e: Empfehlung; anlass: string; primaer: boolean }) {
  return (
    <div className={`rounded-2xl p-5 ${primaer ? "bg-brand-500/8 ring-1 ring-brand-400/25" : "bg-surface-2"}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
        {primaer ? "Unsere Empfehlung" : "Außerdem sinnvoll"}
      </p>
      <h4 className="mt-2 font-display text-lg font-semibold text-fg">{e.titel}</h4>
      <p className="mt-1.5 text-sm leading-relaxed text-fg-muted">{e.text}</p>
      <Link href={mitAnlass(e, anlass)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-ink hover:text-fg">
        {e.cta} <Icon name="arrow-right" size={15} />
      </Link>
    </div>
  );
}

function ErgebnisAnsicht({ e, onNeu }: { e: Ergebnis; onNeu: () => void }) {
  const kontakt = `/kontakt?thema=nis2&anlass=${encodeURIComponent(e.anlass)}`;
  return (
    <div className="card-highlight overflow-hidden" aria-live="polite">
      <div className="border-b border-line bg-brand-500/5 px-6 py-6 md:px-8">
        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ring-1 ${TON[e.key]}`}>
          Ihre Ersteinschätzung
        </span>
        <h3 className="mt-3 font-display text-2xl font-bold leading-tight text-fg md:text-3xl">{e.titel}</h3>
        <p className="mt-2 leading-relaxed text-fg-muted">{e.kurz}</p>
      </div>

      <div className="grid gap-8 p-6 md:p-8">
        <div>
          <h4 className="text-sm font-semibold text-fg">Warum</h4>
          <ul className="mt-3 flex flex-col gap-2.5">
            {e.gruende.map((g) => (
              <li key={g} className="flex items-start gap-2.5 text-sm leading-relaxed text-fg-muted">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-md bg-brand-500" aria-hidden />
                {g}
              </li>
            ))}
          </ul>
        </div>

        {e.pflichten.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-fg">Was damit auf Sie zukommt</h4>
            <ul className="mt-3 flex flex-col gap-2.5">
              {e.pflichten.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm leading-relaxed text-fg-muted">
                  <Icon name="check" size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-accent" />
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              {fristen.map((f) => (
                <div key={f.iso} className="rounded-xl bg-surface-2 p-3.5">
                  <p className="text-xs font-semibold text-brand-ink">{f.datum}</p>
                  <p className="mt-0.5 text-sm font-medium text-fg">{f.titel}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <EmpfehlungKarte e={e.empfehlung} anlass={e.anlass} primaer />
          {e.zweitEmpfehlung && <EmpfehlungKarte e={e.zweitEmpfehlung} anlass={e.anlass} primaer={false} />}
        </div>

        <div className="flex flex-col items-start gap-3 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between">
          <Link
            href={kontakt}
            className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-7 py-3.5 font-semibold text-white shadow-cta transition-all hover:bg-brand-400"
          >
            Ergebnis im Gespräch bestätigen <Icon name="arrow-right" size={18} />
          </Link>
          <button type="button" onClick={onNeu} className="text-sm font-medium text-fg-subtle hover:text-fg">
            Check neu starten
          </button>
        </div>

        <p className="text-xs leading-relaxed text-fg-subtle">
          Unverbindliche Ersteinschätzung auf Basis Ihrer Angaben, keine Rechtsberatung. Maßgeblich sind die
          Einzelfallprüfung und der Gesetzestext des NISG 2026. Stand: September 2026.
        </p>
      </div>
    </div>
  );
}
